/**
 * 通知相关 Store
 * 负责通知列表、未读数量、标记已读/全部已读、软删除等操作
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCacheStore } from '../cache'
import { notificationsApi } from '@/https/api/notifications'
import type {
  NotificationItem,
  NotificationListRequest,
  NotificationListResponse
} from '@/https/types/notifications'

export const useNotificationsStore = defineStore('notifications', () => {
  const cacheStore = useCacheStore()

  // ==================== 状态管理 ====================
  const notifications = ref<NotificationItem[]>([])
  const unreadNotifications = ref<NotificationItem[]>([])
  // 最近系统通知（广播），用于顶部下拉快捷展示
  const recentSystemNotifications = ref<NotificationItem[]>([])

  const pagination = ref<{
    total: number
    page: number
    size: number
    has_more: boolean
  }>({ total: 0, page: 1, size: 20, has_more: false })

  const lastQuery = ref<NotificationListRequest>({})

  const unreadCount = ref<number>(0)

  const listLoading = ref(false)
  const unreadLoading = ref(false)
  const countLoading = ref(false)
  const recentLoading = ref(false)
  const operationLoading = ref(false)

  const listError = ref<string | null>(null)
  const unreadError = ref<string | null>(null)
  const countError = ref<string | null>(null)
  const operationError = ref<string | null>(null)
  const recentError = ref<string | null>(null)

  // 轮询计时器（仅用于未读数量轻量轮询）
  let countTimer: number | null = null

  // ==================== 计算属性 ====================
  const hasNotifications = computed(() => notifications.value.length > 0)
  const hasUnread = computed(() => unreadCount.value > 0)

  const formattedNotifications = computed(() => {
    return notifications.value.map(item => ({
      ...item,
      // 可以在此添加格式化字段，如截断内容等
    }))
  })

  // ==================== 数据加载 ====================
  const loadNotifications = async (params?: NotificationListRequest): Promise<boolean> => {
    try {
      listLoading.value = true
      listError.value = null

      const query = params ?? lastQuery.value
      lastQuery.value = query

      const result: NotificationListResponse = await cacheStore.smartCache(
        'notifications:list',
        () => notificationsApi.list(query),
        true // 列表默认强制刷新
      )

      notifications.value = result.items
      pagination.value = {
        total: result.total,
        page: result.page,
        size: result.size,
        has_more: result.has_more
      }

      return true
    } catch (error) {
      console.error('加载通知列表失败:', error)
      listError.value = error instanceof Error ? error.message : '加载通知列表失败'
      return false
    } finally {
      listLoading.value = false
    }
  }

  const fetchNotificationDetail = async (id: number): Promise<NotificationItem | null> => {
    const cached = notifications.value.find(n => n.id === id)
    if (cached) return cached

    const cachedRecent = recentSystemNotifications.value.find(n => n.id === id)
    if (cachedRecent) return cachedRecent

    try {
      const item = await notificationsApi.get(id)
      const exists = notifications.value.some(n => n.id === item.id)
      if (!exists) notifications.value = [item, ...notifications.value]
      return item
    } catch (error) {
      console.error('获取通知详情失败:', error)
      const ok = await loadNotifications({ page: 1, size: 200 })
      if (!ok) return null
      return notifications.value.find(n => n.id === id) || null
    }
  }

  const loadUnreadNotifications = async (): Promise<boolean> => {
    try {
      unreadLoading.value = true
      unreadError.value = null

      const res = await notificationsApi.listUnread()
      // 处理分页响应格式：提取 items 字段
      let items: NotificationItem[] = []
      if (Array.isArray(res)) {
        // 兼容旧实现：若后端返回数组
        items = res as unknown as NotificationItem[]
      } else if (res && typeof res === 'object' && 'items' in res) {
        items = Array.isArray((res as NotificationListResponse).items)
          ? (res as NotificationListResponse).items
          : []
      }
      unreadNotifications.value = items
      return true
    } catch (error) {
      console.error('加载未读通知失败:', error)
      unreadError.value = error instanceof Error ? error.message : '加载未读通知失败'
      return false
    } finally {
      unreadLoading.value = false
    }
  }

  // 加载最近系统通知（广播 audience==='all' 或类型为 system），仅取前10条
  const loadRecentSystemNotifications = async (): Promise<boolean> => {
    try {
      recentLoading.value = true
      recentError.value = null
      const res = await notificationsApi.list({ page: 1, size: 20 })
      const items = Array.isArray(res.items) ? res.items : []
      recentSystemNotifications.value = items
        .filter(n => (n.audience === 'all') || n.notification_type === 'system')
        .slice(0, 10)
      return true
    } catch (error) {
      recentSystemNotifications.value = []
      recentError.value = null
      return true
    } finally {
      recentLoading.value = false
    }
  }

  const fetchUnreadCount = async (): Promise<number> => {
    try {
      countLoading.value = true
      countError.value = null
      const stats = await notificationsApi.getUnreadCount()
      // 与后端 /unread-count/ 对齐（返回 { count }）
      unreadCount.value = (stats as unknown as { count?: number }).count ?? 0
      return unreadCount.value
    } catch (error) {
      console.error('获取未读数量失败:', error)
      countError.value = error instanceof Error ? error.message : '获取未读数量失败'
      return 0
    } finally {
      countLoading.value = false
    }
  }

  // ==================== 操作方法 ====================
  const markRead = async (id: number): Promise<boolean> => {
    try {
      operationLoading.value = true
      operationError.value = null
      // 仅允许单用户通知标记已读
      const target = notifications.value.find(n => n.id === id)
      if (target && target.audience === 'all') {
        operationError.value = '广播通知不支持标记已读'
        return false
      }
      await notificationsApi.markRead(id)

      // 本地更新
      if (target) target.is_read = true
      unreadNotifications.value = unreadNotifications.value.filter(n => n.id !== id)
      if (unreadCount.value > 0) unreadCount.value -= 1
      return true
    } catch (error) {
      console.error('标记已读失败:', error)
      operationError.value = error instanceof Error ? error.message : '标记已读失败'
      return false
    } finally {
      operationLoading.value = false
    }
  }

  const markAllRead = async (): Promise<boolean> => {
    try {
      operationLoading.value = true
      operationError.value = null
      // 仅对单用户通知执行“全部已读”，不触发广播通知的回执写入
      const targets = notifications.value.filter(n => n.audience === 'user' && !n.is_read)
      await Promise.all(targets.map(n => notificationsApi.markRead(n.id)))

      // 本地更新
      notifications.value.forEach(n => {
        if (n.audience === 'user') n.is_read = true
      })
      unreadNotifications.value = unreadNotifications.value.filter(n => n.audience !== 'user')
      // 重新计算未读数量（仅统计单用户通知）
      unreadCount.value = notifications.value.filter(n => n.audience === 'user' && !n.is_read).length
      return true
    } catch (error) {
      console.error('标记全部已读失败:', error)
      operationError.value = error instanceof Error ? error.message : '标记全部已读失败'
      return false
    } finally {
      operationLoading.value = false
    }
  }

  const softDeleteNotification = async (id: number): Promise<boolean> => {
    try {
      operationLoading.value = true
      operationError.value = null
      // 仅允许单用户通知删除（软删除）
      const target = notifications.value.find(n => n.id === id)
      if (target && target.audience === 'all') {
        operationError.value = '广播通知不支持删除'
        return false
      }
      await notificationsApi.softDelete(id)

      // 本地删除
      notifications.value = notifications.value.filter(n => n.id !== id)
      unreadNotifications.value = unreadNotifications.value.filter(n => n.id !== id)
      return true
    } catch (error) {
      console.error('删除通知失败:', error)
      operationError.value = error instanceof Error ? error.message : '删除通知失败'
      return false
    } finally {
      operationLoading.value = false
    }
  }

  // ==================== 轮询管理（轻量） ====================
  const startUnreadCountPolling = (intervalMs = 15000): void => {
    stopUnreadCountPolling()
    countTimer = window.setInterval(() => {
      void fetchUnreadCount()
    }, intervalMs)
  }

  const stopUnreadCountPolling = (): void => {
    if (countTimer) {
      window.clearInterval(countTimer)
      countTimer = null
    }
  }

  // ==================== 初始化 & 清理 ====================
  const initNotifications = async (): Promise<void> => {
    stopUnreadCountPolling()
    await Promise.all([
      fetchUnreadCount(),
      loadUnreadNotifications(),
      loadRecentSystemNotifications()
    ])
  }

  const clearNotificationErrors = (): void => {
    listError.value = null
    unreadError.value = null
    countError.value = null
    operationError.value = null
    recentError.value = null
  }

  const clearNotifications = (): void => {
    stopUnreadCountPolling()
    notifications.value = []
    unreadNotifications.value = []
    recentSystemNotifications.value = []
    unreadCount.value = 0
    pagination.value = { total: 0, page: 1, size: 20, has_more: false }
    lastQuery.value = {}
    clearNotificationErrors()
  }

  // ==================== 返回 API ====================
  return {
    // 状态
    notifications: computed(() => notifications.value),
    unreadNotifications: computed(() => unreadNotifications.value),
    recentSystemNotifications: computed(() => recentSystemNotifications.value),
    pagination: computed(() => pagination.value),
    unreadCount: computed(() => unreadCount.value),
    listLoading: computed(() => listLoading.value),
    unreadLoading: computed(() => unreadLoading.value),
    countLoading: computed(() => countLoading.value),
    recentLoading: computed(() => recentLoading.value),
    operationLoading: computed(() => operationLoading.value),
    listError: computed(() => listError.value),
    unreadError: computed(() => unreadError.value),
    countError: computed(() => countError.value),
    operationError: computed(() => operationError.value),
    recentError: computed(() => recentError.value),
    hasNotifications,
    hasUnread,
    formattedNotifications,

    // 方法
    loadNotifications,
    fetchNotificationDetail,
    loadUnreadNotifications,
    loadRecentSystemNotifications,
    fetchUnreadCount,
    markRead,
    markAllRead,
    softDeleteNotification,
    startUnreadCountPolling,
    stopUnreadCountPolling,
    clearNotificationErrors,
    clearNotifications,
    initNotifications
  }
})
